import React, { useState } from 'react';
import times from 'lodash/times';
import { useSelector } from 'react-redux';
import {
  hasAccessToEditProfileSelector,
  hasAccessToLinkAccountsSelector,
  hasPlaidSelector,
  selectAkoyaPhase,
  selectExternalAccountProviderAccess,
} from '../../../../../CommonSelectors/CommonAreaSelector';
import { AddAccountButton } from './AddAccountButton';
import { useAccountsOptionsQuery } from '../../../api/useAccountsOptionsQuery';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Shimmer } from '../../../../../CommonComponents/OPL/Shimmer/Shimmer';
import { ErrorState } from '../../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { EmptyState } from '../../../../../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { useExternalAccountProviderAuthUrlMutation } from '../api/useExternalAccountProviderAuthUrlMutation';
import { ExternalAccountProviderAuthLoading } from './ExternalAccountProviderAuthLoading';
import { FullPageLoading } from '../../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';

interface Props {
  isLoading: boolean;
  plaidOnClick: () => void;
  manualOnClick?: () => void;
  showExternalAccountProviders?: boolean;
  onOpenAkoyaLinkManagementModal?: () => void;
  /** Net Worth add-account flow only — manual accounts without Edit Profile access. */
  allowManualAccountWithoutEditProfile?: boolean;
}

export const AddAccountButtonsGroup: React.FC<Props> = ({
  isLoading,
  plaidOnClick,
  manualOnClick,
  showExternalAccountProviders = false,
  onOpenAkoyaLinkManagementModal = () => {},
  allowManualAccountWithoutEditProfile = false,
}) => {
  const hasAccessToLinkAccounts = useSelector(hasAccessToLinkAccountsSelector);
  const hasPlaid = useSelector(hasPlaidSelector);
  const hasAccessToEditProfile = useSelector(hasAccessToEditProfileSelector);

  const externalAccountProviders = useSelector(selectExternalAccountProviderAccess);

  const akoyaPhase = useSelector(selectAkoyaPhase);

  const { data, isLoading: isLoadingAccountOptions, error, refetch } = useAccountsOptionsQuery();

  const {
    mutateAsync: getAuthUrl,
    isLoading: isGettingAuthUrl,
  } = useExternalAccountProviderAuthUrlMutation();

  const [contextId, setContextId] = useState<string | null>(null);

  if (isLoadingAccountOptions) {
    return (
      <Box display="flex" flexDirection="column" css="gap: 16px">
        {times(3).map(key => (
          <Shimmer key={key} height={68} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box py="16">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const { AllowAddManualAccount: allowAddManualAccount } = data.Options;

  const showManualAccountButton =
    allowAddManualAccount && (hasAccessToEditProfile || allowManualAccountWithoutEditProfile);

  const handleExternalProviderClick = async (url: string) => {
    try {
      const { AuthUrl, ContextId } = await getAuthUrl(url);

      window.open(AuthUrl, '_blank');

      setContextId(ContextId);
    } catch (err) {
      console.error(err);
    }
  };

  const hasPlaidAccountLinking = hasPlaid && hasAccessToLinkAccounts;

  // Only manual accounts -> go to manual account flow (respecting the edit-profile gate)
  if (!hasPlaidAccountLinking && (akoyaPhase === 'none' || !showExternalAccountProviders)) {
    if (showManualAccountButton) {
      manualOnClick?.();
      return null;
    }

    return (
      <Box py="16">
        <EmptyState message="There are no accounts available for you to add." />
      </Box>
    );
  }

  return (
    <>
      {contextId && <ExternalAccountProviderAuthLoading contextId={contextId} />}

      {isGettingAuthUrl && <FullPageLoading />}

      {hasPlaidAccountLinking && (
        <AddAccountButton
          isLoading={isLoading}
          disabled={isLoading}
          legend="The easiest way to add an account."
          icon="fas fa-landmark"
          onClick={plaidOnClick}
        >
          Log in to institution
        </AddAccountButton>
      )}

      {showExternalAccountProviders && akoyaPhase === 'phase3' && (
        <Box as="ul" css="list-style: none" p="0">
          {externalAccountProviders.map(({ name, website, url, imageUrl, id }) => {
            return (
              <Box as="li" key={id}>
                <AddAccountButton
                  disabled={isLoading}
                  legend={website}
                  {...(imageUrl
                    ? { image: { src: imageUrl, alt: '' } }
                    : { icon: 'fas fa-landmark' })}
                  onClick={() => handleExternalProviderClick(url)}
                >
                  Log in to {name}
                </AddAccountButton>
              </Box>
            );
          })}
        </Box>
      )}

      {showManualAccountButton && manualOnClick !== undefined && (
        <AddAccountButton disabled={isLoading} icon="fas fa-plus" onClick={manualOnClick}>
          Add manual account
        </AddAccountButton>
      )}

      {showExternalAccountProviders && akoyaPhase === 'phase2' && (
        <AddAccountButton
          disabled={isLoading}
          icon="far fa-link"
          onClick={onOpenAkoyaLinkManagementModal}
        >
          Add or Manage Linked Accounts
        </AddAccountButton>
      )}
    </>
  );
};
